import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Inbox from './pages/Inbox';
import Setup from './pages/Setup';
import Settings from './pages/Settings';
import Account from './pages/Account';
import Rules from './pages/Rules';
import Snoozed from './pages/Snoozed';
import Connect from './pages/Connect';
import History from './pages/History';
import Pricing from './pages/Pricing';
import AuthCallback from './pages/AuthCallback';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Header from './components/Header';
import { EmailProvider } from './contexts/EmailContext';
import { InstanceProvider, useInstance } from './contexts/InstanceContext';
import { ToastProvider } from './ui/Toast';
import { ConfirmProvider } from './ui/Confirm';
import { ThemeProvider } from './ui/theme';
import { Logo, Alert, Inbox as InboxIcon, Mail } from './ui/icons';
import { CONTACT_EMAIL } from './components/PublicFooter';
import { mailboxService } from './services/api';
import Spinner from './ui/Spinner';
import { isAuthed } from './lib/session';
import { effectiveUiTheme, bootUiTheme } from './lib/uiTheme';
import { HotelThemeProvider, useHotel, loadHotelStyles } from './ui/hotel/HotelTheme';
import { useHotelFonts } from './ui/hotel/useHotelFonts';
import Emblem from './ui/hotel/Emblem';
import Door from './ui/hotel/Door';
import LiftDoors from './components/hotel/LiftDoors';

// The theme the boot screen is drawn in, decided before the instance has
// answered (see bootUiTheme). Read once: the URL and storage it reads from do
// not change while the app boots.
const BOOT_HOTEL = typeof window !== 'undefined' && bootUiTheme(window.location.search) === 'hotel';
// Start the skin and its fonts now rather than after GET /api/config/status:
// the two requests then run side by side. If the server ends up saying
// classic, the chunk is simply never applied.
if (BOOT_HOTEL) loadHotelStyles().catch(() => {});

// The hotel splash is styled inline: it is on screen before the hotel
// stylesheet has arrived, which is the whole point of it.
function HotelBoot({ label }) {
  const night = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
      style={{ background: night ? '#1C1220' : '#F6E3D9', color: night ? '#F3E6DA' : '#2B1B1E' }}
    >
      <div className="animate-fade-up">
        <Emblem size={64} />
      </div>
      <span
        style={{
          fontFamily: "'Bodoni Moda', Didot, 'Times New Roman', serif",
          fontStyle: 'italic',
          fontWeight: 500,
          fontSize: 26,
        }}
      >
        Mailsorter
      </span>
      <span className="flex items-center gap-2 text-sm" style={{ color: night ? '#BFA6A8' : '#6E4B52' }}>
        <Spinner size={16} /> {label}
      </span>
    </div>
  );
}

function BootScreen({ children }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink-50 px-6 text-center">
      {children}
    </div>
  );
}

// What a visitor sees when a HOSTED instance is not wired up.
//
// Until now every public route redirected to /setup, which asks the reader to
// fill in GMAIL_CLIENT_ID, GMAIL_CLIENT_SECRET and GMAIL_REDIRECT_URL and then
// restart the service. On a self-hosted instance that is the right screen: the
// reader owns the deployment. On a hosted one the visitor owns none of it, and
// was handed an environment-variable briefing they could do nothing with.
function Unavailable() {
  return (
    <BootScreen>
      <Logo size={52} />
      <div className="max-w-sm space-y-2">
        <h1 className="font-display text-xl font-bold text-ink-900">Service momentanément indisponible</h1>
        <p className="text-sm text-muted">
          Mailsorter n'est pas encore connecté à Google sur cette instance. La connexion sera
          rétablie dès que l'exploitant aura terminé la configuration.
        </p>
      </div>
      <a href={`mailto:${CONTACT_EMAIL}`} className="btn-secondary">
        <Mail size={16} /> Prévenir l'exploitant
      </a>
    </BootScreen>
  );
}

// A deploy (every push to main) renames every chunk. A tab opened before it
// still runs the old main bundle, asks for a chunk hash that no longer exists,
// and a rejected lazy import unmounts the whole tree. Reloading fetches the new
// index.html and its new hashes. Once only: the flag, set before the reload and
// cleared by the next chunk that loads, stops a chunk that is really missing
// from reloading forever, and without storage there is no flag, so no reload.
const CHUNK_RELOAD_FLAG = 'mailsorter_chunk_reload';

function lazyWithReload(load) {
  return lazy(() =>
    load().then(
      (module) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
        } catch {
          // Storage blocked: there is no flag to clear.
        }
        return module;
      },
      (err) => {
        let reload = false;
        try {
          if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== '1') {
            sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
            reload = true;
          }
        } catch {
          // Storage blocked: a second failure would look like a first, so no reload.
        }
        if (!reload) throw err;
        window.location.reload();
        // Never settles: the fallback stays up until the reload replaces the
        // page, instead of the error screen flashing in between.
        return new Promise(() => {});
      }
    )
  );
}

// Both landings are separate chunks: a visitor downloads only the one this
// instance serves, fonts included.
const ClassicLanding = lazyWithReload(() => import('./pages/Login'));
const HotelLanding = lazyWithReload(() => import('./pages/HotelLanding'));

// The only class component in the app: React catches a render error only in a
// class. It holds the landing chunk that still fails after the one reload, so
// the visitor gets a way out instead of a blank page. React already logs the
// error it caught; this only decides what to render.
class LandingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <BootScreen>
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-danger-50 text-danger-600">
          <Alert size={28} />
        </span>
        <div className="max-w-sm space-y-2">
          <h1 className="text-xl font-bold text-ink-900">La page n'a pas pu se charger.</h1>
        </div>
        <button onClick={() => window.location.reload()} className="btn-primary">
          Recharger
        </button>
      </BootScreen>
    );
  }
}

// The landing on /, chosen at runtime from UI_THEME (see lib/uiTheme.js), so
// the Grand Hotel theme can be unplugged without a rebuild.
function Landing() {
  const { uiTheme } = useInstance();
  const { search } = useLocation();
  const theme = useMemo(() => effectiveUiTheme(uiTheme, search), [uiTheme, search]);
  const Page = theme === 'hotel' ? HotelLanding : ClassicLanding;
  return (
    <LandingErrorBoundary>
      <Suspense
        fallback={
          <BootScreen>
            <Spinner size={18} className="text-brand-600" />
          </BootScreen>
        }
      >
        <Page />
      </Suspense>
    </LandingErrorBoundary>
  );
}

// RequireAuth is the single place the session is checked. It used to be a
// per-page copy-paste that four of the six screens simply skipped: they fired
// their API calls, took a 401, and the axios interceptor recovered with a full
// browser reload, losing all state and flashing the login page.
function RequireAuth({ children }) {
  const navigate = useNavigate();
  const authed = isAuthed();

  useEffect(() => {
    if (!authed) navigate('/', { replace: true });
  }, [authed, navigate]);

  return authed ? children : null;
}

function RequireMailbox({ children }) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(() => Boolean(localStorage.getItem('hasMailbox')));
  const [checking, setChecking] = useState(() => !localStorage.getItem('hasMailbox'));

  useEffect(() => {
    if (localStorage.getItem('hasMailbox')) {
      setReady(true);
      setChecking(false);
      return;
    }
    let cancelled = false;
    mailboxService
      .get()
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.mailbox) {
          localStorage.setItem('hasMailbox', 'true');
          setReady(true);
        } else {
          localStorage.removeItem('hasMailbox');
          navigate('/connect', { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (checking) {
    return (
      <BootScreen>
        <div className="flex items-center gap-3 text-muted">
          <Spinner size={18} className="text-brand-600" />
          <span className="text-sm font-medium">Chargement de votre boîte...</span>
        </div>
      </BootScreen>
    );
  }

  return ready ? children : null;
}

// The page under the header. The hotel leaves it transparent so the striped
// wallpaper on <body> shows through, and opens the lift doors on every change
// of floor.
function AppFrame({ children }) {
  const hotel = useHotel();
  if (!hotel) return <div className="min-h-screen bg-ink-50">{children}</div>;
  return (
    <div className="min-h-screen">
      <LiftDoors />
      {children}
    </div>
  );
}

function NotFound() {
  const navigate = useNavigate();
  const hotel = useHotel();
  if (hotel) {
    return (
      <div className="hd-empty mx-auto flex min-h-[60vh] max-w-lg justify-center">
        <Door number="404" color="#7A2E3B" crop className="hd-empty__art" />
        <h1 className="hd-title mt-5">Page introuvable</h1>
        <p>Cette adresse ne correspond à aucun écran de Mailsorter.</p>
        <button onClick={() => navigate('/inbox')} className="btn-primary hd-empty__act">
          Retour à ma boîte
        </button>
      </div>
    );
  }
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-6 text-center">
      <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-ink-100 text-ink-600">
        <InboxIcon size={26} />
      </span>
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">Page introuvable</h1>
      <p className="mt-2 text-sm text-muted">
        Cette adresse ne correspond à aucun écran de Mailsorter.
      </p>
      <button onClick={() => navigate('/inbox')} className="btn-primary mt-6">
        Retour à ma boîte
      </button>
    </div>
  );
}

function App() {
  // The deployment is probed once, in InstanceProvider, and read here. Pricing
  // and the header read the same value instead of asking again.
  const { loading, error, isConfigured, isUsable, selfHosted, reload } = useInstance();
  useHotelFonts(BOOT_HOTEL);

  if (loading && BOOT_HOTEL) return <HotelBoot label="Démarrage de Mailsorter..." />;

  if (loading) {
    return (
      <BootScreen>
        <div className="animate-fade-up">
          <Logo size={56} />
        </div>
        <div className="flex items-center gap-3 text-muted">
          <Spinner size={18} className="text-brand-600" />
          <span className="text-sm font-medium">Démarrage de Mailsorter...</span>
        </div>
      </BootScreen>
    );
  }

  if (error && !isUsable) {
    return (
      <BootScreen>
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-danger-50 text-danger-600">
          <Alert size={28} />
        </span>
        <div className="max-w-sm space-y-2">
          <h1 className="text-xl font-bold text-ink-900">Le moteur ne répond pas</h1>
          <p className="text-sm text-muted">{error} Vérifiez que le backend tourne, puis réessayez.</p>
        </div>
        <button onClick={reload} className="btn-primary">
          Réessayer
        </button>
      </BootScreen>
    );
  }

  // Unconfigured: the owner of a self-hosted instance gets the setup briefing,
  // a visitor on a hosted one gets an honest "unavailable" instead of a list of
  // environment variables they do not control.
  const unconfigured = () => (selfHosted ? <Navigate to="/setup" replace /> : <Unavailable />);
  // Gated on "is there a way in at all", not on "does Google work here": an
  // instance whose only door is a mailbox is a working instance, and calling it
  // unconfigured made the hosted edition impossible to enter.
  const guard = (element, requireMailbox = false) => (isUsable ? <RequireAuth>{requireMailbox ? <RequireMailbox>{element}</RequireMailbox> : element}</RequireAuth> : unconfigured());

  return (
    <Router>
      <HotelThemeProvider fallback={<HotelBoot label="Démarrage de Mailsorter..." />}>
        <ToastProvider>
          <ConfirmProvider>
            <EmailProvider>
              <AppFrame>
                <a
                  href="#main"
                  className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded-lg focus:bg-brand-fill focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
                >
                  Aller au contenu
                </a>
                <Header />
                <main id="main">
                  <Routes>
                    {/* /setup stays reachable by its address whatever the
                        edition: it is the operator's screen, and an operator on a
                        hosted instance still needs it. It is simply no longer
                        where a visitor is sent, and on an instance that signs
                        people in by mailbox it no longer swallows the landing
                        page either. */}
                    <Route
                      path="/setup"
                      element={isConfigured ? <Navigate to="/" replace /> : <Setup onComplete={reload} />}
                    />
                    <Route path="/" element={isUsable ? <Landing /> : unconfigured()} />
                    <Route path="/inbox" element={guard(<Inbox />, true)} />
                    <Route path="/rules" element={guard(<Rules />, true)} />
                    <Route path="/snoozed" element={guard(<Snoozed />, true)} />
                    <Route path="/history" element={guard(<History />, true)} />
                    <Route path="/settings" element={guard(<Settings />)} />
                    {/* Brancher une boite par IMAP. Garde comme les autres: on
                        branche une boite SUR un compte, donc il faut deja etre
                        connecte a Mailsorter. */}
                    <Route path="/connect" element={guard(<Connect />)} />
                    <Route path="/account" element={guard(<Account />)} />
                    {/* A self-hosted instance bills nobody, so there is no pricing
                        page to land on: the route redirects rather than rendering
                        an empty one. */}
                    <Route path="/pricing" element={selfHosted ? <Navigate to="/" replace /> : <Pricing />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    {/* Public and unconditional. Google's OAuth verification
                        expects the privacy policy to be reachable from the home
                        page, so it cannot depend on the instance being wired. */}
                    <Route path="/confidentialite" element={<Privacy />} />
                    <Route path="/conditions" element={<Terms />} />
                    {/* Redirects for legacy routes */}
                    <Route path="/emails" element={<Navigate to="/inbox" replace />} />
                    <Route path="/triage" element={<Navigate to="/inbox" replace />} />
                    {/* Anything else used to render a blank page under the header. */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </AppFrame>
            </EmailProvider>
          </ConfirmProvider>
        </ToastProvider>
      </HotelThemeProvider>
    </Router>
  );
}

// The theme must wrap the boot screens too, otherwise the first thing a
// dark-theme user sees is a full-white splash.
export default function AppWithTheme() {
  return (
    <ThemeProvider>
      <InstanceProvider>
        <App />
      </InstanceProvider>
    </ThemeProvider>
  );
}
